import { useState } from "react";
import { Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { toUiTask, type UiTask } from "@/components/tasks/types";
import type {
	Sprint,
	StartSprintResponse,
} from "@/services/sprint.service";
import { listAllTasks } from "@/services/task.service";
import { usePermission } from "@/hooks/usePermission";
import { StartSprintModal } from "./StartSprintModal";

const ALLOWED_ROLES = new Set(["admin", "manager"]);

interface Props {
	nextSprint: Sprint | null;
	onStarted: (result: StartSprintResponse) => void;
}

export function StartSprintButton({ nextSprint, onStarted }: Props) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [candidateTasks, setCandidateTasks] = useState<UiTask[]>([]);
	const { roleKey } = usePermission();
	if (!nextSprint) return null;
	if (!roleKey || !ALLOWED_ROLES.has(roleKey)) return null;

	// Candidates: every open task not already in the sprint. Fetched fresh on
	// open since the board only holds the filtered tasks.
	async function handleOpen() {
		setLoading(true);
		try {
			const tasks = await listAllTasks({
				exclude_status: "done,cancelled",
			});
			setCandidateTasks(
				tasks
					.filter((t) => t.sprint_id !== nextSprint!.id)
					.map(toUiTask),
			);
			setOpen(true);
		} catch {
			toast.error("Failed to load tasks", {
				description: "Please try again.",
			});
		} finally {
			setLoading(false);
		}
	}

	return (
		<>
			<Button
				variant="outline"
				className="flex items-center gap-2"
				onClick={handleOpen}
				disabled={loading}
			>
				{loading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Play className="h-4 w-4" />
				)}
				Start Sprint
			</Button>
			<StartSprintModal
				open={open}
				onClose={() => setOpen(false)}
				sprint={nextSprint}
				candidateTasks={candidateTasks}
				onStarted={onStarted}
			/>
		</>
	);
}
